import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, ListOrdered, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usePlanMode } from "../hooks/usePlanMode";

interface SequentialOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  routines: any[];
  planId: string;
  /** If true, the dialog will activate "sequential" mode on confirm. */
  activateModeOnConfirm?: boolean;
  onConfirmed?: () => void;
}

export const SequentialOrderDialog = ({
  open,
  onOpenChange,
  routines,
  planId,
  activateModeOnConfirm = false,
  onConfirmed,
}: SequentialOrderDialogProps) => {
  const { setMode, reorderRoutines } = usePlanMode(planId);
  const [items, setItems] = useState(routines);

  useEffect(() => {
    if (open) setItems(routines);
  }, [open, routines]);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = Array.from(items);
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
  };

  const isSaving = reorderRoutines.isPending || setMode.isPending;

  const handleConfirm = async () => {
    try {
      await reorderRoutines.mutateAsync(items.map((r) => r.id));
      if (activateModeOnConfirm) {
        await setMode.mutateAsync("sequential");
      }
      onConfirmed?.();
      onOpenChange(false);
    } catch (e) {
      // toasts handled in hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListOrdered className="h-5 w-5 text-primary" />
            Definí el orden de las rutinas
          </DialogTitle>
          <DialogDescription>
            En modo secuencial, las rutinas se entrenan en este orden. Usá las flechas
            para reorganizarlas. La N° 1 será la próxima a entrenar.
          </DialogDescription>
        </DialogHeader>

        {items.length === 0 ? (
          <div className="text-center py-6 text-primary/60 text-sm">
            Este plan todavía no tiene rutinas.
          </div>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {items.map((routine, index) => (
              <div
                key={routine.id}
                className="flex items-center gap-3 rounded-lg border border-primary/20 bg-card p-3"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-heading text-xs font-bold">
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-primary truncate text-sm">
                    {routine.name}
                  </p>
                  <p className="text-xs text-primary/60">
                    {routine.exercises?.length ?? 0} ejercicios
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => move(index, -1)}
                    disabled={index === 0 || isSaving}
                    aria-label="Subir"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => move(index, 1)}
                    disabled={index === items.length - 1 || isSaving}
                    aria-label="Bajar"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isSaving || items.length === 0}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {activateModeOnConfirm ? "Activar modo secuencial" : "Guardar orden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
